import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import {
  JOURNAL_UNITS,
  copyText,
  filterLines,
  journalArgs,
  journalTargets,
  linesFromPayload,
  mergeLines,
  summaryText,
  type FleetBoxLite,
  type JournalFilter,
  type JournalLine,
  type JournalUnit,
} from "@/lib/jobJournal";

const LINE_COLOR: Record<string, string> = {
  WARN: "text-yellow-700 dark:text-yellow-400",
  ERROR: "text-destructive",
  FATAL: "text-destructive font-semibold",
  ERR: "text-destructive",
  CRIT: "text-destructive font-semibold",
};

function errMsg(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/**
 * Runner journal for ONE job: the `pure-runner` (or `pure-publish`) journal
 * lines carrying this job id — including the Firecracker guest console —
 * read WITHOUT sudo through the deploy-agent op `read-job-journal` (broker
 * `POST /v1/fleet/diagnose`, SSO + operator, relayed over the AgentHub
 * WebSocket). The job row has no runner host, so every connected runner box
 * is asked and the lines are merged. Lines arrive redacted.
 */
export function JobJournalPanel({
  jobId,
  jobCreatedAt,
  terminal,
}: {
  jobId: string;
  jobCreatedAt: number | null;
  /** The job has finished: no live tail. */
  terminal: boolean;
}) {
  const token = useAuthToken();
  const [unit, setUnit] = useState<JournalUnit>("pure-runner");
  const [lineCount, setLineCount] = useState(500);
  const [loaded, setLoaded] = useState<{
    lines: JournalLine[];
    total: number;
    hosts: number;
    truncated: string[];
    errors: string[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<JournalFilter>({
    query: "",
    console: "all",
    problemsOnly: false,
  });
  const [tail, setTail] = useState(true);
  const [copied, setCopied] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ov = await api<{ ok: true; boxes: FleetBoxLite[] }>(
        token,
        "/fleet/overview",
      );
      const targets = journalTargets(ov.boxes, unit);
      if (targets.length === 0) {
        setLoaded(null);
        setError(
          unit === "pure-runner"
            ? "No connected runner box to ask (see the Fleet page)."
            : "No connected publish host to ask (see the Fleet page).",
        );
        return;
      }
      const results = await Promise.all(
        targets.map(async (b) => {
          try {
            const r = await api<{
              ok: boolean;
              payload: unknown;
              reason: string | null;
            }>(token, "/fleet/diagnose", {
              method: "POST",
              body: JSON.stringify({
                box_id: b.box_id,
                op: "read-job-journal",
                args: journalArgs(jobId, unit, lineCount, jobCreatedAt),
              }),
            });
            if (!r.ok) return { b, error: r.reason ?? "failed" };
            const p = r.payload as {
              truncated?: string;
              matched?: number;
            } | null;
            return {
              b,
              lines: linesFromPayload(b.hostname || b.box_id, r.payload),
              truncated: p?.truncated,
              matched: p?.matched ?? 0,
            };
          } catch (e) {
            return { b, error: errMsg(e, "request failed") };
          }
        }),
      );
      const lines = mergeLines(
        results.map((r): JournalLine[] => ("lines" in r && r.lines) || []),
      );
      setLoaded({
        lines,
        total: lines.length,
        hosts: targets.length,
        truncated: results.flatMap((r) =>
          "truncated" in r && r.truncated ? [r.truncated] : [],
        ),
        errors: results.flatMap((r) =>
          "error" in r ? [`${r.b.hostname || r.b.box_id}: ${r.error}`] : [],
        ),
      });
    } catch (e) {
      setError(errMsg(e, "failed to read the journal"));
    } finally {
      setLoading(false);
    }
  }, [token, jobId, unit, lineCount, jobCreatedAt]);

  // Nothing is read until the operator asks (each read scans the journal on
  // the runner hosts). Once opened: load, then refresh every 5 s while the
  // job is still running and "tail" is on.
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    if (opened) void load();
  }, [opened, load]);
  useEffect(() => {
    if (!opened || terminal || !tail) return;
    const t = setInterval(() => void load(), 5_000);
    return () => clearInterval(t);
  }, [opened, terminal, tail, load]);

  const shown = useMemo(
    () => (loaded ? filterLines(loaded.lines, filter) : []),
    [loaded, filter],
  );
  useEffect(() => {
    const el = preRef.current;
    if (tail && el) el.scrollTop = el.scrollHeight;
  }, [shown, tail]);

  return (
    <Card data-testid="job-journal">
      <CardHeader>
        <CardTitle>Runner journal</CardTitle>
        <CardDescription>
          The journal lines the runner logged for this job, including the
          Firecracker guest console — read through the deploy-agent (no sudo),
          tokens redacted.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {!opened && (
          <Button variant="secondary" onClick={() => setOpened(true)}>
            load runner journal
          </Button>
        )}
        {opened && (
          <div className="flex flex-wrap items-center gap-2">
            <Select
              className="w-36 h-8 text-sm"
              value={unit}
              onChange={(e) => setUnit(e.target.value as JournalUnit)}
              aria-label="systemd unit"
            >
              {JOURNAL_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </Select>
            <Select
              className="w-28 h-8 text-sm"
              value={String(lineCount)}
              onChange={(e) => setLineCount(Number(e.target.value))}
              aria-label="lines"
            >
              {[200, 500, 1000, 2000].map((n) => (
                <option key={n} value={n}>
                  last {n}
                </option>
              ))}
            </Select>
            <Input
              className="h-8 w-56 text-sm"
              placeholder="search…"
              value={filter.query}
              onChange={(e) => setFilter({ ...filter, query: e.target.value })}
            />
            <Select
              className="w-40 h-8 text-sm"
              value={filter.console}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  console: e.target.value as JournalFilter["console"],
                })
              }
              aria-label="guest console"
            >
              <option value="all">all lines</option>
              <option value="console">guest console only</option>
              <option value="no-console">hide guest console</option>
            </Select>
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={filter.problemsOnly}
                onChange={(e) =>
                  setFilter({ ...filter, problemsOnly: e.target.checked })
                }
              />
              warnings+
            </label>
            <label
              className="flex items-center gap-1 text-xs"
              title="Scroll to the newest line and, while the job runs, refresh every 5 s"
            >
              <input
                type="checkbox"
                checked={tail}
                onChange={(e) => setTail(e.target.checked)}
              />
              tail
            </label>
            <Button
              variant="secondary"
              size="sm"
              disabled={loading}
              onClick={() => void load()}
            >
              {loading ? "loading…" : "refresh"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={shown.length === 0}
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(copyText(shown))
                  .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  })
                  .catch(() => undefined);
              }}
            >
              {copied ? "copied" : "copy"}
            </Button>
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        {loaded && (
          <p className="text-xs text-muted-foreground">
            {summaryText({
              shown: shown.length,
              total: loaded.total,
              hosts: loaded.hosts,
              truncated: loaded.truncated,
              errors: loaded.errors,
            })}
          </p>
        )}
        {loaded && loaded.total === 0 && !error && (
          <p className="text-sm text-muted-foreground">
            No journal lines carry this job id (the job may not have reached a
            runner yet, or the journal has rotated).
          </p>
        )}
        {shown.length > 0 && (
          <pre
            ref={preRef}
            className="bg-muted rounded-md p-3 text-xs overflow-auto max-h-[32rem] whitespace-pre-wrap break-words"
          >
            {shown.map((l, i) => (
              <div
                key={i}
                className={l.level ? LINE_COLOR[l.level] : undefined}
              >
                {l.text}
              </div>
            ))}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}
